export interface AppointmentCoordinates {
  lat: number;
  lng: number;
}

export interface ContactLocationCoordinateSource {
  latitude?: unknown;
  longitude?: unknown;
  coordinates?: unknown;
}

export interface LocationAddressSource {
  location_address?: unknown;
  address?: unknown;
  address_line1?: unknown;
  address_line2?: unknown;
  suburb?: unknown;
  city?: unknown;
  province?: unknown;
  state?: unknown;
  postal_code?: unknown;
  zip?: unknown;
  zip_code?: unknown;
  country?: unknown;
}

function normalizeAddressPart(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

export function normalizeCoordinateValue(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

export function normalizeCoordinatesObject(value: unknown): AppointmentCoordinates | null {
  if (!value || typeof value !== 'object') return null;

  const source = value as {
    lat?: unknown;
    lng?: unknown;
    latitude?: unknown;
    longitude?: unknown;
    lon?: unknown;
    long?: unknown;
    x?: unknown;
    y?: unknown;
  };
  const lat = normalizeCoordinateValue(source.lat ?? source.latitude ?? source.y);
  const lng = normalizeCoordinateValue(source.lng ?? source.longitude ?? source.lon ?? source.long ?? source.x);

  if (lat == null || lng == null) return null;

  return { lat, lng };
}

export function parseCoordinatesString(raw?: string | null): AppointmentCoordinates | null {
  if (!raw) return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  const pointMatch = trimmed.match(/POINT\(([-\d.]+)\s+([-\d.]+)\)/i);
  if (pointMatch) {
    const lng = normalizeCoordinateValue(pointMatch[1]);
    const lat = normalizeCoordinateValue(pointMatch[2]);
    if (lat != null && lng != null) return { lat, lng };
  }

  const numericParts = trimmed
    .replace(/[()]/g, '')
    .split(',')
    .map((part) => normalizeCoordinateValue(part));

  if (numericParts.length === 2 && numericParts[0] != null && numericParts[1] != null) {
    return { lat: numericParts[0], lng: numericParts[1] };
  }

  return null;
}

export function extractCoordinates(value: unknown): AppointmentCoordinates | null {
  if (!value) return null;
  if (typeof value === 'string') return parseCoordinatesString(value);
  return normalizeCoordinatesObject(value);
}

export function getContactLocationCoordinates(location?: ContactLocationCoordinateSource | null): AppointmentCoordinates | null {
  if (!location) return null;

  const lat = normalizeCoordinateValue(location.latitude);
  const lng = normalizeCoordinateValue(location.longitude);
  if (lat != null && lng != null) {
    return { lat, lng };
  }

  return extractCoordinates(location.coordinates);
}

export function hasCoordinates(value: unknown): boolean {
  return extractCoordinates(value) != null;
}

export function hasContactLocationCoordinates(location?: ContactLocationCoordinateSource | null): boolean {
  return getContactLocationCoordinates(location) != null;
}

export function formatLocationAddress(parts: Array<unknown> | LocationAddressSource | null | undefined): string {
  if (!parts) return '';

  if (Array.isArray(parts)) {
    return parts
      .map(normalizeAddressPart)
      .filter(Boolean)
      .join(', ');
  }

  if (typeof parts === 'object') {
    const source = parts as LocationAddressSource;
    const directAddress = normalizeAddressPart(source.location_address) || normalizeAddressPart(source.address);

    if (directAddress) {
      return directAddress;
    }

    return [
      source.address_line1,
      source.address_line2,
      source.suburb,
      source.city,
      source.province ?? source.state,
      source.postal_code ?? source.zip ?? source.zip_code,
      source.country,
    ]
      .map(normalizeAddressPart)
      .filter(Boolean)
      .join(', ');
  }

  return '';
}

export function buildMissingCoordinatesMessage(locationLabel?: string | null): string {
  return locationLabel?.trim()
    ? `"${locationLabel.trim()}" does not have coordinates. Select a saved location with coordinates or choose a custom map location.`
    : 'This location does not have coordinates. Select a saved location with coordinates or choose a custom map location.';
}
